import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { LogOut, Loader2, Lock, Inbox, MailOpen, RefreshCw } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-ink placeholder:text-muted-foreground/60 transition-[border-color,box-shadow] duration-200 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Er ging iets mis. Probeer het opnieuw.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm font-semibold text-ink">{value}</dd>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.92l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.27 14.27A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.27v-3.1H1.29a12 12 0 0 0 0 10.64l3.98-3z" />
      <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.63l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z" />
    </svg>
  );
}

export default function Beheer() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authError, setAuthError] = useState(location.state?.authError || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("aanvragen");
  const [requests, setRequests] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  const loadData = async () => {
    setDataLoading(true);
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/admin/requests`, { withCredentials: true }),
        axios.get(`${API}/admin/contacts`, { withCredentials: true }),
      ]);
      setRequests(r.data);
      setContacts(c.data);
    } catch (e) {
      if (e.response?.status === 401) setUser(false);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setAuthError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/beheer";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const doLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    setRequests([]);
    setContacts([]);
  };

  const shownError = error || authError;

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEO title="Beheer | Directelabels" description="Beveiligd beheerdersoverzicht van Directelabels." path="/beheer" noIndex />
      <header className="border-b border-black/5 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <Link to="/" data-testid="beheer-logo" className="flex items-center gap-2.5">
            <LogoMark className="h-9 w-9" />
            <span className="font-display text-base font-semibold tracking-tight text-ink">Directelabels · Beheer</span>
          </Link>
          {user && (
            <button
              type="button"
              data-testid="beheer-logout"
              onClick={doLogout}
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-xs font-bold text-ink transition-colors duration-200 hover:border-primary hover:text-primary"
            >
              <LogOut size={14} />
              Uitloggen
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 md:py-16">
        {user === null && (
          <div className="flex justify-center py-20">
            <Loader2 size={28} className="animate-spin text-primary" />
          </div>
        )}

        {user === false && (
          <div className="mx-auto max-w-md">
            <div className="rounded-3xl border border-black/5 bg-white p-8 shadow-sm md:p-10">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-energy-pale text-primary">
                <Lock size={24} />
              </span>
              <h1 className="mt-6 text-center font-display text-2xl font-semibold tracking-tight text-ink">Inloggen beheer</h1>
              <p className="mt-2 text-center text-sm text-muted-foreground">Alleen voor de beheerder van Directelabels.</p>

              <button
                type="button"
                data-testid="beheer-google-login"
                onClick={loginGoogle}
                className="mt-8 flex w-full items-center justify-center gap-2.5 rounded-full border border-ink/15 bg-white px-6 py-3.5 text-sm font-bold text-ink transition-colors duration-200 hover:border-primary hover:text-primary"
              >
                <GoogleIcon />
                Inloggen met Google
              </button>

              <div className="my-6 flex items-center gap-3 text-xs font-semibold text-muted-foreground" aria-hidden="true">
                <span className="h-px flex-1 bg-black/10" />
                of met e-mail en wachtwoord
                <span className="h-px flex-1 bg-black/10" />
              </div>

              <form onSubmit={submitLogin} data-testid="beheer-login-form" className="space-y-5">
                <div>
                  <label htmlFor="beheer-email" className="mb-2 block text-sm font-bold text-ink">E-mailadres</label>
                  <input id="beheer-email" data-testid="beheer-input-email" type="email" required className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                </div>
                <div>
                  <label htmlFor="beheer-password" className="mb-2 block text-sm font-bold text-ink">Wachtwoord</label>
                  <input id="beheer-password" data-testid="beheer-input-password" type="password" required className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
                </div>
                {shownError && (
                  <p data-testid="beheer-login-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                    {shownError}
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="beheer-login-submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-energy-light disabled:opacity-60"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {loading ? "Bezig..." : "Inloggen"}
                </button>
              </form>
            </div>
          </div>
        )}

        {user && (
          <div data-testid="beheer-dashboard">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="font-display text-3xl font-semibold tracking-tighter text-ink md:text-4xl">Overzicht</h1>
                <p className="mt-1 text-sm text-muted-foreground">Ingelogd als {user.name ? `${user.name} · ` : ""}{user.email}</p>
              </div>
              <button
                type="button"
                data-testid="beheer-refresh"
                onClick={loadData}
                disabled={dataLoading}
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-bold text-ink transition-colors duration-200 hover:border-primary hover:text-primary disabled:opacity-50"
              >
                <RefreshCw size={15} className={dataLoading ? "animate-spin" : ""} />
                Vernieuwen
              </button>
            </div>

            <div className="mt-8 flex gap-3">
              <button
                type="button"
                data-testid="beheer-tab-aanvragen"
                onClick={() => setTab("aanvragen")}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200 ${
                  tab === "aanvragen" ? "bg-primary text-white" : "border border-ink/15 text-ink hover:border-primary hover:text-primary"
                }`}
              >
                <Inbox size={15} />
                Aanvragen ({requests.length})
              </button>
              <button
                type="button"
                data-testid="beheer-tab-contact"
                onClick={() => setTab("contact")}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200 ${
                  tab === "contact" ? "bg-primary text-white" : "border border-ink/15 text-ink hover:border-primary hover:text-primary"
                }`}
              >
                <MailOpen size={15} />
                Contactberichten ({contacts.length})
              </button>
            </div>

            <div className="mt-8 space-y-4">
              {tab === "aanvragen" &&
                (requests.length === 0 ? (
                  <p data-testid="beheer-aanvragen-empty" className="rounded-2xl border border-dashed border-black/15 bg-white p-10 text-center text-sm text-muted-foreground">
                    Nog geen aanvragen binnengekomen.
                  </p>
                ) : (
                  requests.map((r) => (
                    <article key={r.request_id} data-testid={`beheer-request-${r.request_id}`} className="rounded-2xl border border-black/5 bg-white p-6 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-display text-base font-semibold text-ink">
                          {r.voornaam} {r.achternaam} <span className="ml-2 rounded-full bg-energy-pale px-3 py-0.5 text-xs font-bold text-energy-deep">{r.request_id}</span>
                        </p>
                        <p className="text-xs font-medium text-muted-foreground">{fmtDate(r.created_at)}</p>
                      </div>
                      <dl className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                        <Row label="Woningtype" value={r.woningtype} />
                        <Row label="Prijsindicatie" value={r.prijsindicatie} />
                        <Row label="Adres" value={`${r.straat} ${r.huisnummer}, ${r.postcode} ${r.plaats}`} />
                        <Row label="Gewenste datum" value={r.voorkeursdatum} />
                        <Row label="Bouwjaar" value={r.bouwjaar} />
                        <Row label="Oppervlakte" value={r.woonoppervlakte ? `${r.woonoppervlakte} m²` : ""} />
                        <Row label="E-mail" value={r.email} />
                        <Row label="Telefoon" value={r.telefoon} />
                      </dl>
                      {r.opmerking && <p className="mt-4 rounded-xl bg-muted px-4 py-3 text-sm text-muted-foreground">{r.opmerking}</p>}
                    </article>
                  ))
                ))}

              {tab === "contact" &&
                (contacts.length === 0 ? (
                  <p data-testid="beheer-contact-empty" className="rounded-2xl border border-dashed border-black/15 bg-white p-10 text-center text-sm text-muted-foreground">
                    Nog geen contactberichten binnengekomen.
                  </p>
                ) : (
                  contacts.map((c) => (
                    <article key={c.message_id} data-testid={`beheer-contact-${c.message_id}`} className="rounded-2xl border border-black/5 bg-white p-6 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-display text-base font-semibold text-ink">
                          {c.naam} <span className="ml-2 rounded-full bg-secondary px-3 py-0.5 text-xs font-bold text-energy-deep">{c.onderwerp}</span>
                        </p>
                        <p className="text-xs font-medium text-muted-foreground">{fmtDate(c.created_at)}</p>
                      </div>
                      <dl className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                        <Row label="E-mail" value={c.email} />
                        <Row label="Telefoon" value={c.telefoon} />
                        <Row label="Adres woning" value={c.adres ? `${c.adres}${c.postcode ? `, ${c.postcode}` : ""} ${c.plaats || ""}`.trim() : ""} />
                        <Row label="Woningtype" value={c.woningtype} />
                        <Row label="Oppervlakte" value={c.woonoppervlakte ? `${c.woonoppervlakte} m²` : ""} />
                        <Row label="Gewenste datum" value={c.gewenste_datum} />
                      </dl>
                      {c.bericht && <p className="mt-4 rounded-xl bg-muted px-4 py-3 text-sm text-muted-foreground">{c.bericht}</p>}
                    </article>
                  ))
                ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
